/**
 * Pill — PAQVERO's official button family (design-system sheet).
 * One pill shape, shared everywhere: storefront, PRIME, admin actions.
 *
 * variant: amber | charcoal | outline | text | success | danger | neutral | warning | prime | primeLight
 * size:    sm (32) | md (40) | lg (48) | xl (56)
 *
 * <Pill variant="charcoal" size="lg" leftIcon={<Cart/>}>Add to Cart</Pill>
 */
const VARIANTS = {
  amber:
    "bg-[#C88732] text-[#FFFEFA] shadow-[0_2px_10px_rgba(200,135,50,0.28)] hover:bg-[#A96C1E] active:bg-[#8A561A] disabled:bg-[#EBD9BC] disabled:text-[#FFFEFA] disabled:shadow-none",
  charcoal:
    "bg-[#20221F] text-[#F8F6F0] shadow-[0_3px_12px_rgba(32,34,31,0.25)] hover:bg-[#2E302C] active:bg-[#151614] disabled:bg-[#C9C7BC] disabled:text-[#F8F6F0] disabled:shadow-none",
  outline:
    "border border-[#E6E1D7] bg-[#FFFEFA] text-[#20221F] hover:border-[#C88732]/50 hover:bg-[#C88732]/[0.08] hover:text-[#A96C1E] active:bg-[#C88732]/[0.14] disabled:text-[#A5A49A] disabled:bg-[#F8F6F0]",
  text:
    "text-[#20221F] hover:text-[#A96C1E] disabled:text-[#A5A49A]",
  success:
    "bg-[#287A55] text-[#FFFEFA] hover:bg-[#226B4B] active:bg-[#1C563E] disabled:bg-[#BCD8CA] disabled:shadow-none",
  danger:
    "bg-[#B9473F] text-[#FFFEFA] hover:bg-[#9E3831] active:bg-[#822D28] disabled:bg-[#E5C4C1] disabled:shadow-none",
  neutral:
    "bg-[#77776F] text-[#FFFEFA] hover:bg-[#63635C] active:bg-[#4F4F49] disabled:bg-[#C9C7BC] disabled:shadow-none",
  warning:
    "bg-[#C88732] text-[#FFFEFA] hover:bg-[#A96C1E] disabled:bg-[#EBD9BC] disabled:shadow-none",
  prime:
    "bg-[#242521] text-[#E4B66D] shadow-[0_0_0_1px_rgba(228,182,109,0.35),0_6px_20px_-6px_rgba(228,182,109,0.45)] hover:bg-[#151614] hover:shadow-[0_0_0_1px_rgba(228,182,109,0.55),0_8px_26px_-6px_rgba(228,182,109,0.6)] active:bg-black disabled:text-[#8d8468] disabled:shadow-none",
  primeLight:
    "bg-[#E4B66D] text-[#242521] shadow-[0_4px_16px_-4px_rgba(228,182,109,0.6)] hover:bg-[#EFC582] active:bg-[#C89A4B] disabled:bg-[#F0E2C8] disabled:text-[#9a917a] disabled:shadow-none",
};

const SIZES = {
  sm: "h-8 gap-1.5 px-3.5 text-xs",
  md: "h-10 gap-2 px-5 text-sm",
  lg: "h-12 gap-2 px-6 text-sm",
  xl: "h-14 gap-2.5 px-8 text-base",
};

export function Pill({
  variant = "amber",
  size = "md",
  leftIcon,
  rightIcon,
  className = "",
  children,
  as: Comp = "button",
  ...props
}) {
  const bare = variant === "text";
  return (
    <Comp
      className={`inline-flex select-none items-center justify-center whitespace-nowrap font-bold transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C88732] disabled:cursor-not-allowed ${
        bare ? "" : `rounded-full ${SIZES[size]}`
      } ${VARIANTS[variant]} ${className}`}
      {...props}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </Comp>
  );
}
